import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import ExcelJS from "exceljs";
import DropzoneComponent from "../components/Utils/DropzoneComponent";
import OverlayLoading from "../components/OverlayLoading";
import { toast } from "react-toastify";
import { colors } from "../global";
import Title from "../components/title";
import UploadedItemCard from "../components/UniversalBenchmarkCalculator/uploadedItemCard";
import { apiFetch } from "../apiClient";
const UniversalBenchmarkCalculator = () => {
  const [file, setFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [metadata, setMetadata] = useState({
    file_name: null,
    sheet_information: {
      sheet_name: null,
      sheet_id: null,
      from: null,
      to: null,
      first_data_row: 1,
      range_weight_break_source: null,
    },
    zone_location: {
      sheet_name: null,
      sheet_id: null,
      header_row: 1,
    },
  });

  const onDrop = useCallback(async (acceptedFiles) => {
    setIsSubmitting(true);
    const newFiles = await Promise.all(
      acceptedFiles.map(async (file) => {
        const originalName = file.name;
        const fileExtension = originalName.split(".").pop();
        const fileName = originalName.replace(`.${fileExtension}`, "");

        const now = new Date();
        const timestamp = now
          .toISOString()
          .replace(/[-:]/g, "")
          .replace("T", "_")
          .split(".")[0];
        const newFileName = `${fileName}_${timestamp}.${fileExtension}`;
        let sheetList = [];
        if (fileExtension === "xlsx") {
          try {
            const buffer = await file.arrayBuffer();
            const workbook = new ExcelJS.Workbook();
            await workbook.xlsx.load(buffer);

            workbook.eachSheet((worksheet, sheetId) => {
              sheetList.push({ information: worksheet, id: sheetId });
            });
          } catch (error) {
            console.error("Error reading Excel file:", error);
          }
        }

        return {
          originalFile: file,
          renamedFile: new File([file], newFileName, { type: file.type }),
          sheetList,
        };
      })
    );

    setFile(...newFiles);
    if (newFiles && newFiles[0].renamedFile.name) {
      setMetadata((prev) => ({
        ...prev,
        file_name: newFiles[0].renamedFile.name,
      }));
    }
    setIsSubmitting(false);
  }, []);

  const handleMetadataUpdate = (metadata) => {
    setMetadata(metadata);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ".xlsx",
    multiple: false,
  });

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("file", file.renamedFile);
      formData.append("metadata", JSON.stringify(metadata));

      const response = await apiFetch(
        `/api/v1/universalBenchmarkCalculator/upload`,
        {
          method: "POST",
          body: formData,
          credentials: "include",
        }
      );

      if (!response.ok) {
        let errorMessage = "Upload failed.";
        try {
          const errorResponse = await response.json();
          if (errorResponse.detail) {
            errorMessage = errorResponse.detail;
          } else if (errorResponse.error) {
            errorMessage = errorResponse.error;
          }
        } catch (parseError) {
          console.error("Failed to parse error response", parseError);
        }

        toast.error(errorMessage);
        return;
      }
      const now = new Date();
      const timestamp = now
        .toISOString()
        .replace(/[-:]/g, "")
        .replace("T", "_")
        .split(".")[0];

      const contentType = response.headers.get("Content-Type");

      if (
        response.ok &&
        contentType &&
        contentType.includes(
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        )
      ) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `Universal Benchmark Calculator_${timestamp}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
      } else {
        const errorText = await response.text();
        console.error("Download failed. Response not Excel:", errorText);
        toast.error(
          "Upload succeeded, but the server returned an unexpected format."
        );
      }
    } catch (err) {
      if (!err.message.includes("Upload failed.")) {
        toast.error("Upload failed. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      {isSubmitting && (
        <OverlayLoading
          isVisible={isSubmitting}
          message="Processing your request..."
          icon="truck"
        />
      )}
      <div className=" p-6 pb-0">
        <Title title={"Universal Benchmark Calculator"} />
      </div>
      <div className="m-4 p-6 bg-white rounded-xl border border-gray-100 shadow-sm">
        <DropzoneComponent
          getInputProps={getInputProps}
          getRootProps={getRootProps}
          isDragActive={isDragActive}
          fileTypes="XLSX"
        />
      </div>
      <div className="space-y-6 p-4 pb-4">
        {/* File List */}
        {file && (
          <div className="space-y-4">
            <div className="space-y-3">
              <UploadedItemCard
                key={file.renamedFile.name}
                file={file.renamedFile}
                sheetList={file.sheetList}
                onUpdateMetadata={handleMetadataUpdate}
                metadata={metadata}
              />
            </div>
          </div>
        )}
      </div>
      {file && (
        <div className="flex justify-end pt-4 space-y-6 p-4 pb-4">
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="py-2 px-6 rounded-md font-medium shadow-sm transition-all flex items-center justify-center hover:cursor-pointer"
            style={{
              backgroundColor: isSubmitting ? "#ccc" : colors.accent,
              color: colors.white,
              opacity: isSubmitting ? 0.7 : 1,
            }}
          >
            {isSubmitting ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Processing...
              </>
            ) : (
              "Convert Files"
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default UniversalBenchmarkCalculator;
